import classNames from "classnames";
import {
	Context,
	createContext,
	forwardRef,
	PropsWithChildren,
	ReactElement,
	Ref,
	RefObject,
	UIEvent,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	MediaQueryKind,
	useLocalStorageState,
	useResizeObserver,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { setTrackingId } from "client/Misc/utils";
import { mergeRefs } from "client/Misc/reactUtils";
import { BusyIndicator } from "../BusyIndicator/BusyIndicator";
import styles from "./ContentWithSidebar.module.less";
import { PinButton } from "../PinButton/PinButton";

type ContentWithSidebarContextData = {
	trackingId?: string;
};

const ContentWithSidebarContext: Context<ContentWithSidebarContextData> = createContext<ContentWithSidebarContextData>({});

export type ContentWithSidebarProps = {
	hasBusyIndicator?: boolean;
	trackingId?: string;
};

export const ContentWithSidebar = (props: PropsWithChildren<ContentWithSidebarProps>): ReactElement => {
	const {
		hasBusyIndicator,
		children,
		trackingId
	} = props;

	const contextData = useMemo(
		(): ContentWithSidebarContextData => ({ trackingId }),
		[trackingId]
	);

	return (
		<div
			className={styles.root}
			{...setTrackingId(trackingId)}
		>
			<ContentWithSidebarContext.Provider value={contextData}>
				{ children}
			</ContentWithSidebarContext.Provider>
			{ hasBusyIndicator && (
				<BusyIndicator />
			)}
		</div>
	);
};
ContentWithSidebar.displayName = "ContentWithSidebar";

export type SidebarHandle = {
	onClose(): void;
};

export type SidebarProps = {
	persistentKeyName?: string;
};

const Sidebar = forwardRef<SidebarHandle, PropsWithChildren<SidebarProps>>(
	(
		props: PropsWithChildren<SidebarProps>,
		ref: Ref<SidebarHandle>
	): ReactElement => {
		const {
			children,
			persistentKeyName
		} = props;

		const [expansionPreference, setExpansionPreference] =
			useLocalStorageState<boolean | undefined>(persistentKeyName);

		const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

		const [isSideBarExpanded, setIsSideBarExpanded] = useState<boolean>(
			() => isMobile
				? false
				: expansionPreference ?? true
		);

		const { trackingId } = useContext(ContentWithSidebarContext);

		const expansionHandler = useCallback(
			(isExpanded: boolean): void => {
				setIsSideBarExpanded(isExpanded);
				setExpansionPreference(isExpanded);
			},
			[setExpansionPreference]
		);

		useImperativeHandle(
			ref,
			() => ({
				onClose: () => expansionHandler(false)
			}),
			[expansionHandler]
		);

		return (
			<div
				className={styles.sidebarHost}
				{...setTrackingId(trackingId, "__sidebar")}
			>
				<div
					className={classNames(
						styles.sidebar,
						{
							[styles.sidebar_collapsed]: !isSideBarExpanded
						}
					)}
				>
					<div className={styles.sidebarContentWrapper}>
						{ children }
					</div>
				</div>
				<div className={styles.expandButtonContainer}>
					<PinButton
						onClick={() => expansionHandler(!isSideBarExpanded)}
						isPinned={isSideBarExpanded}
						trackingId="collapse-expand"
					/>
				</div>
			</div>
		);
	}
);
Sidebar.displayName = "ContentWithSidebar.Sidebar";
ContentWithSidebar.Sidebar = Sidebar;

export type ContentWithSidebarContentScrollEventArgs = {
	scrollPosition: number;
	scrollableArea: number;
};

export type ContentWithSidebarContentProps = {
	shouldPreserveScrollBarSpace?: boolean;
	onScroll?(args: ContentWithSidebarContentScrollEventArgs): void;
	hasNoPadding?: boolean;
	hasBackgroundColor?: boolean;
};

const Content = forwardRef<HTMLDivElement, PropsWithChildren<ContentWithSidebarContentProps>>(
	(
		props: PropsWithChildren<ContentWithSidebarContentProps>,
		ref: Ref<HTMLDivElement>
	): ReactElement => {
		const {
			onScroll,
			shouldPreserveScrollBarSpace = false,
			hasNoPadding = false,
			hasBackgroundColor = false,
			children
		} = props;

		const { trackingId } = useContext(ContentWithSidebarContext);
		const containerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
		const { height } = useResizeObserver(containerRef);
		const [scrollTop, setScrollTop] = useState<number>(0);

		const scrollHandler = useCallback(
			(event: UIEvent<HTMLDivElement>): void => {
				setScrollTop(event.currentTarget.scrollTop);
			},
			[]
		);

		useEffect(
			() => {
				if (!onScroll || !containerRef.current) {
					return;
				}

				const scrollHeight: number = containerRef.current.scrollHeight;
				const scrollableArea: number = scrollHeight - height;

				onScroll({
					scrollPosition: scrollTop,
					scrollableArea
				});
			},
			[height, onScroll, scrollTop]
		);

		return (
			<div
				className={classNames(
					styles.content,
					{
						[styles.content_preserveScrollBarSpace]: shouldPreserveScrollBarSpace,
						[styles.content_noPadding]: hasNoPadding,
						[styles.content_colored]: hasBackgroundColor
					}
				)}
				ref={mergeRefs(ref, containerRef)}
				onScroll={scrollHandler}
				{...setTrackingId(trackingId, "__content")}
			>
				{children}
			</div>
		);
	}
);
Content.displayName = "ContentWithSidebar.Content";
ContentWithSidebar.Content = Content;

const ContentWithSidebarActionBar = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;

	return (
		<div className={styles.actionBar}>
			{children}
		</div>
	);
};
ContentWithSidebarActionBar.displayName = "ContentWithSidebarActionBar";
ContentWithSidebar.ActionBar = ContentWithSidebarActionBar;
